/**
 * MilestoneList Component
 *
 * Displays milestones for a project with card/table view,
 * progress bar, status badges, and kebab menus.
 *
 * @module components/milestones/MilestoneList
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  Dropdown,
  Spinner,
  Alert,
  ProgressBar,
  Modal,
} from 'react-bootstrap';
import { DataTable } from '../common/DataTable';
import KebabMenu from '../common/KebabMenu';
import { toast } from '../../utils/toast';
import { formatDate, formatCurrency, formatPercentage } from '../../utils/formatters';
import { getProjectMilestones, createMilestone, updateMilestone, completeMilestone, deleteMilestone } from '../../services/milestoneService';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../common/ViewToggle';
import MilestoneForm from './MilestoneForm';
import MilestoneProgressBar from './MilestoneProgressBar';
import { useAuth } from '../../contexts/AuthContext';

const statusInfo = {
  pending: { label: 'Pendiente', variant: 'secondary' },
  in_progress: { label: 'En Progreso', variant: 'primary' },
  completed: { label: 'Completado', variant: 'success' },
  cancelled: { label: 'Cancelado', variant: 'danger' },
};

const MilestoneList = ({ projectId }) => {
  const queryClient = useQueryClient();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const canEdit = isAdmin || isTechnician;

  const [showForm, setShowForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);

  const { data: milestonesData, isLoading, error } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => getProjectMilestones(projectId),
    enabled: !!projectId,
  });

  const milestones = milestonesData?.data || [];

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      if (editingMilestone) {
        const result = await updateMilestone(editingMilestone.id, formData);
        if (result.error) throw result.error;
        toast.success('Hito actualizado exitosamente');
      } else {
        const result = await createMilestone({ ...formData, project_id: projectId });
        if (result.error) throw result.error;
        toast.success('Hito creado exitosamente');
      }
      setShowForm(false);
      setEditingMilestone(null);
      queryClient.invalidateQueries(['milestones', projectId]);
    } catch (err) {
      console.error('Save milestone error:', err);
      toast.error('Error al guardar el hito');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (milestone) => {
    try {
      const result = await completeMilestone(milestone.id);
      if (result.error) throw result.error;
      toast.success('Hito completado exitosamente');
      queryClient.invalidateQueries(['milestones', projectId]);
    } catch (err) {
      toast.error('Error al completar el hito');
    }
  };

  const handleDelete = async () => {
    if (!milestoneToDelete) return;
    try {
      const result = await deleteMilestone(milestoneToDelete.id);
      if (result.error) throw result.error;
      toast.success('Hito eliminado exitosamente');
      setShowDeleteModal(false);
      setMilestoneToDelete(null);
      queryClient.invalidateQueries(['milestones', projectId]);
    } catch (err) {
      toast.error('Error al eliminar el hito');
    }
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
    setShowForm(true);
  };

  const milestoneColumns = useMemo(() => {
    const cols = [
      {
        id: 'order',
        header: '#',
        accessor: 'order_index',
        minWidth: 60,
        maxWidth: 80,
      },
      {
        id: 'title',
        header: 'Titulo',
        accessor: 'title',
        minWidth: 200,
        cell: (m) => (
          <>
            <strong>{m.title}</strong>
            {m.description && <small className="d-block text-muted">{m.description}</small>}
          </>
        ),
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: 'status',
        minWidth: 120,
        cell: (m) => {
          const si = statusInfo[m.status] || statusInfo.pending;
          return <Badge bg={si.variant}>{si.label}</Badge>;
        },
      },
      {
        id: 'weight',
        header: 'Peso',
        accessor: (m) => m.weight_percentage || 0,
        minWidth: 90,
        cell: (m) => formatPercentage(m.weight_percentage),
      },
      {
        id: 'tasks',
        header: 'Tareas',
        accessor: (m) => m.taskProgress || 0,
        minWidth: 120,
        cell: (m) => m.taskCount > 0 ? (
          <span>
            {m.completedTaskCount}/{m.taskCount}
            <ProgressBar
              now={m.taskProgress}
              style={{ height: '4px', minWidth: '40px' }}
              className="mt-1"
            />
          </span>
        ) : <span className="text-muted">-</span>,
      },
      {
        id: 'target_date',
        header: 'Fecha Objetivo',
        accessor: 'target_date',
        minWidth: 130,
        cell: (m) => formatDate(m.target_date),
      },
      {
        id: 'billing',
        header: 'Facturacion',
        accessor: (m) => m.billing_amount || 0,
        align: 'right',
        minWidth: 130,
        cell: (m) => m.billable
          ? (m.billing_amount
              ? formatCurrency(m.billing_amount)
              : `${formatPercentage(m.weight_percentage)} del total`)
          : <span className="text-muted">No facturable</span>,
      },
    ];

    if (canEdit) {
      cols.push({
        id: 'actions',
        header: '',
        sortable: false,
        resizable: false,
        align: 'center',
        noSelect: true,
        minWidth: 50,
        maxWidth: 60,
        cell: (m) => (
          <div data-no-select onClick={(e) => e.stopPropagation()}>
            <KebabMenu label={`Acciones de ${m.title}`}>
              <Dropdown.Item onClick={() => handleEdit(m)}>
                <i className="bi bi-pencil me-2"></i>Editar
              </Dropdown.Item>
              {m.status !== 'completed' && (
                <Dropdown.Item onClick={() => handleComplete(m)}>
                  <i className="bi bi-check-circle me-2"></i>Completar
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => { setMilestoneToDelete(m); setShowDeleteModal(true); }}
              >
                <i className="bi bi-trash me-2"></i>Eliminar
              </Dropdown.Item>
            </KebabMenu>
          </div>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Cargando hitos...</span>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">Error al cargar hitos: {error.message}</Alert>;
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-flag me-2"></i>
            Hitos del Proyecto
          </h5>
          <div className="d-flex gap-2">
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
            {canEdit && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditingMilestone(null);
                  setShowForm(true);
                }}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Nuevo Hito
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {/* Progress overview */}
          {milestones.length > 0 && (
            <div className="mb-4">
              <small className="text-muted d-block mb-1">Progreso General</small>
              <MilestoneProgressBar milestones={milestones} />
            </div>
          )}

          {milestones.length === 0 ? (
            <p className="text-muted text-center py-4">
              No hay hitos definidos para este proyecto.
            </p>
          ) : isTableView ? (
            <DataTable
              tableId="milestones"
              data={milestones}
              rowKey={(m) => m.id}
              columns={milestoneColumns}
            />
          ) : (
            <Row className="g-3">
              {milestones.map((m) => {
                const si = statusInfo[m.status] || statusInfo.pending;
                return (
                  <Col xs={12} sm={6} lg={4} key={m.id}>
                    <Card className="shadow-sm h-100">
                      <Card.Body style={{ position: 'relative' }}>
                        {canEdit && (
                          <KebabMenu
                            label={`Acciones de ${m.title}`}
                            stopPropagation={false}
                            dropdownProps={{ style: { position: 'absolute', top: '12px', right: '12px' } }}
                          >
                            <Dropdown.Item onClick={() => handleEdit(m)}>
                              <i className="bi bi-pencil me-2"></i>Editar
                            </Dropdown.Item>
                            {m.status !== 'completed' && (
                              <Dropdown.Item onClick={() => handleComplete(m)}>
                                <i className="bi bi-check-circle me-2"></i>Completar
                              </Dropdown.Item>
                            )}
                            <Dropdown.Divider />
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => {
                                setMilestoneToDelete(m);
                                setShowDeleteModal(true);
                              }}
                            >
                              <i className="bi bi-trash me-2"></i>Eliminar
                            </Dropdown.Item>
                          </KebabMenu>
                        )}

                        <div className="d-flex align-items-center gap-2 mb-2 pe-4">
                          <Badge bg="light" text="dark" className="fw-normal">#{m.order_index}</Badge>
                          <h6 className="mb-0 fw-bold">{m.title}</h6>
                        </div>

                        <Badge bg={si.variant} className="mb-2">{si.label}</Badge>

                        {m.description && (
                          <p className="small text-muted mb-2">{m.description}</p>
                        )}

                        <div className="d-flex justify-content-between small mb-2">
                          <span className="text-muted">Peso: {formatPercentage(m.weight_percentage)}</span>
                          {m.target_date && (
                            <span className="text-muted">
                              <i className="bi bi-calendar3 me-1"></i>
                              {formatDate(m.target_date)}
                            </span>
                          )}
                        </div>

                        {m.taskCount > 0 && (
                          <div className="mb-2">
                            <small className="text-muted">
                              Tareas: {m.completedTaskCount}/{m.taskCount}
                            </small>
                            <ProgressBar now={m.taskProgress} style={{ height: '6px' }} />
                          </div>
                        )}

                        {m.billable && (
                          <div className="border-top pt-2 mt-2">
                            <small className="text-muted">Facturacion: </small>
                            <small className="fw-bold">
                              {m.billing_amount
                                ? formatCurrency(m.billing_amount)
                                : `${formatPercentage(m.weight_percentage)} del total`
                              }
                            </small>
                          </div>
                        )}

                        {m.deliverables && m.deliverables.length > 0 && (
                          <div className="border-top pt-2 mt-2">
                            <small className="text-muted d-block mb-1">Entregables:</small>
                            {m.deliverables.slice(0, 3).map((d, idx) => (
                              <small key={idx} className="d-block">
                                <i className={`bi ${d.completed ? 'bi-check-circle-fill text-success' : 'bi-circle'} me-1`}></i>
                                {d.title}
                              </small>
                            ))}
                            {m.deliverables.length > 3 && (
                              <small className="text-muted">+{m.deliverables.length - 3} mas</small>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Milestone Form Modal */}
      <MilestoneForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingMilestone(null);
        }}
        onSave={handleSave}
        milestone={editingMilestone}
        loading={saving}
      />

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Estas seguro de eliminar el hito <strong>{milestoneToDelete?.title}</strong>?</p>
          <p className="text-danger mb-0">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Las tareas asociadas perderan su vinculacion al hito.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="bi bi-trash me-1"></i>Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MilestoneList;
